import { getAppDeviceInfo } from "~/components/app";
import { parseUA } from "~/utils/common";
import eventBus from "~/utils/eventBus";
import { getChannel } from "~/utils/getChannel";

export const COLD_START_TAGS_TAB_SWITCH_EVENT = "cold-start-tags-tab-switch";
export const COLD_START_TAGS_CONFIRMED_EVENT = "cold-start-tags-confirmed";

const COLD_START_TAGS_PENDING_KEY = "cold_start_tags_pending";
const COLD_START_TAGS_SHOWN_KEY = "cold_start_tags_shown";
let pendingColdStartTagsAutoTrigger = false;

const isClient = () => import.meta.client && typeof window !== "undefined";

const readFirstValidValue = (...values) => {
	const target = values.find(value => value !== undefined && value !== null && value !== "");
	return target === undefined ? "" : String(target);
};

const normalizePlatform = rawPlatform => {
	const platform = String(rawPlatform || "").toLowerCase();
	const ua = parseUA();

	if (platform.includes("ios") || platform.includes("iphone") || platform.includes("ipad")) {
		return "iOS";
	}

	if (platform.includes("android")) {
		return "Android";
	}

	if (platform.includes("web") || platform.includes("windows") || platform.includes("mac")) {
		return "Web";
	}

	if (ua.ios) return "iOS";
	if (ua.android) return "Android";

	return "Web";
};

const normalizeOsVersion = deviceInfo => {
	return readFirstValidValue(
		deviceInfo.os_version,
		deviceInfo.osVersion,
		deviceInfo.system_version,
		deviceInfo.systemVersion
	);
};

export const markColdStartTagsPending = () => {
	if (!isClient()) return;

	localStorage.setItem(COLD_START_TAGS_PENDING_KEY, "1");
	localStorage.removeItem(COLD_START_TAGS_SHOWN_KEY);
};

export const clearColdStartTagsPending = () => {
	if (!isClient()) return;

	localStorage.removeItem(COLD_START_TAGS_PENDING_KEY);
	localStorage.removeItem(COLD_START_TAGS_SHOWN_KEY);
	pendingColdStartTagsAutoTrigger = false;
};

export const hasPendingColdStartTags = () => {
	if (!isClient()) return false;

	return localStorage.getItem(COLD_START_TAGS_PENDING_KEY) === "1";
};

export const hasShownColdStartTags = () => {
	if (!isClient()) return false;

	return localStorage.getItem(COLD_START_TAGS_SHOWN_KEY) === "1";
};

export const markColdStartTagsShown = () => {
	if (!isClient()) return;

	localStorage.setItem(COLD_START_TAGS_SHOWN_KEY, "1");
	pendingColdStartTagsAutoTrigger = false;
};

const shouldQueueColdStartTagsAutoTrigger = () => {
	return isClient() && window.innerWidth >= 768;
};

export const requestColdStartTagsAutoTrigger = () => {
	if (shouldQueueColdStartTagsAutoTrigger()) {
		pendingColdStartTagsAutoTrigger = true;
	}

	eventBus.emit(COLD_START_TAGS_TAB_SWITCH_EVENT);
};

export const consumeColdStartTagsAutoTrigger = () => {
	const shouldTrigger = pendingColdStartTagsAutoTrigger;
	pendingColdStartTagsAutoTrigger = false;
	return shouldTrigger;
};

export const shouldCheckColdStartTagsOnSquareEnter = () => {
	if (!isClient() || window.innerWidth < 768) return false;
	if (!hasPendingColdStartTags()) return false;
	if (hasShownColdStartTags()) return false;

	return true;
};

export const tryShowColdStartTagsByAiSquare = isAiSquare => {
	if (!shouldCheckColdStartTagsOnSquareEnter()) return false;
	if (Number(isAiSquare) !== 0) return false;

	markColdStartTagsShown();
	requestColdStartTagsAutoTrigger();
	return true;
};

export const buildColdStartTagsTrackPayload = (selectedTags, userId = "") => {
	if (!isClient()) return [];

	const deviceInfo = getAppDeviceInfo() || {};
	const connection =
		navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};

	return [
		{
			event_id: "select_cold_start_tags",
			timestamp: Date.now(),
			extend: {
				cold_start_tags: Array.isArray(selectedTags) ? selectedTags : [],
			},
			device_id: readFirstValidValue(
				deviceInfo.device_id,
				deviceInfo.deviceId,
				deviceInfo.idfa,
				deviceInfo.imei
			),
			app_version: readFirstValidValue(
				localStorage.getItem("app_version"),
				deviceInfo.app_version,
				deviceInfo.appVersion
			),
			platform: normalizePlatform(
				readFirstValidValue(
					deviceInfo.platform,
					deviceInfo.platform_name,
					localStorage.getItem("app_system")
				)
			),
			os_version: normalizeOsVersion(deviceInfo),
			network_type: readFirstValidValue(
				deviceInfo.network_type,
				deviceInfo.networkType,
				connection.effectiveType,
				connection.type
			),
			user_id: readFirstValidValue(userId),
			location: "",
			register_channel: getChannel(),
		},
	];
};
